// 手机模式门店详情 —— 复刻 Android 截图（深蓝 header + 设备 chip 筛选）
// 路由：/m/video/:storeId
// type_id == 7 → 语音设备；其他 → 摄像头（来自反编译 DEVICE_TYPE_AUDIO=7 / NVR=2）
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Hls from 'hls.js';
import { msePlayer, type MsePlayerInstance } from '../utils/mse-player.js';
import { Alert, Button, Card, Empty, Modal, Input, Drawer, Popover, message as antdMessage, Spin, Tabs, Tag, Typography } from 'antd';
import { StarFilled, FileTextOutlined, CustomerServiceOutlined, DeleteOutlined } from '@ant-design/icons';
import {
	LeftOutlined,
	EnvironmentOutlined,
	ClockCircleOutlined,
	PhoneOutlined,
	ToolOutlined,
	StarOutlined,
	MoreOutlined,
	CopyOutlined,
	FilterOutlined,
	AppstoreOutlined,
	UnorderedListOutlined,
	SettingOutlined,
	VideoCameraOutlined,
	AudioOutlined,
	RightOutlined,
	CloudUploadOutlined,
} from '@ant-design/icons';

const RightIcon = () => <RightOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />;
// 1×1 透明 GIF poster — 抑制 Android WebView 的灰色「无内容」播放占位 UI
const BLANK_VIDEO_POSTER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
import { callInternal } from '../api/internal.js';
import { fetchApacCollectedStoreIds, setApacStoreCollected } from '../api/apac-store.js';
import { apiUrl, isCapacitor } from '../utils/api-base.js';
import { deviceLabel } from '../utils/device-label.js';
import { fetchChannels, renameChannel, type Channel } from '../api/device-channel.js';
import { fetchFastChannels, fastChannelToDevice } from '../api/fast-device.js';
import { deleteStore } from '../api/repair.js';
import { useGlobalStore } from '../stores/global-store.js';
import StoreOverview from './StoreOverview.js';
import ApacStoreDataView from './ApacStoreData.js';
import MobileSubPageScaffold, { SubPageCard, SubPageSection } from '../components/MobileSubPageScaffold.js';
import { colors } from '../theme/colors.js';
import { formatDate } from '../utils/locale-format.js';
import { useRegion } from '../hooks/useRegion.js';
import { mobileColor, mobileFontSize, mobileFontWeight, mobileRadius, mobileShadow } from '../theme/mobile-typography.js';

const SERVICE_HOTLINE = '4001-668-099';

const PRIMARY = '#FF7043';
const AUDIO_TYPE_ID = '7';

interface Store {
	store_id: string;
	store: string;
	store_code: string;
	addr: string;
	business_hours: string;
	store_phone?: string;
	shopowner_phone?: string;
	more_phone?: string[];
	create_time?: string | number;
	create_at?: string | number;
	has_device: number;
	store_status: number;
	[k: string]: unknown;
}

interface Device {
	device_id: number | string;     // 通道 auto_id（用于 channel_edit）
	sn?: string;                    // 真 SN，喂 /play/weburl
	name?: string;                  // 通道别名
	store_id?: string;
	status?: number | string;       // '1' = online（通道粒度）
	type_id?: number | string;
	channel_id?: string;            // '1' / '2' ...（NVR 多通道）
	auto_id?: string;
	device_status?: number | string;
	offline_time?: string | null;
	has_yuzhiweis?: number | string;
}

type StatusFilter = 'all' | 'online' | 'offline';
type CatFilter = 'all' | 'camera' | 'audio';

export default function StoreDetail() {
	const { storeId } = useParams<{ storeId: string }>();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const region = useRegion();
	const storeNameFallback = searchParams.get('storeName') ?? '';
	const requestedStatus = searchParams.get('status');
	const [store, setStore] = useState<Store | null>(null);
	const [devices, setDevices] = useState<Device[] | null>(null);
	const [storeError, setStoreError] = useState<string | null>(null);
	const [deviceError, setDeviceError] = useState<string | null>(null);
	const [topTab, setTopTab] = useState<'devices' | 'overview'>('devices');
	const [statusFilter, setStatusFilter] = useState<StatusFilter>(
		requestedStatus === 'offline' || requestedStatus === 'online' ? requestedStatus : 'all',
	);
	const [catFilter, setCatFilter] = useState<CatFilter>('all');
	const [viewMode, setViewMode] = useState<'list' | 'preview'>('list');

	useEffect(() => {
		if (!storeId) return;
		setStoreError(null);
		callInternal<Store[]>({ short_name: 'base', action: '/store/visible_stores' })
			.then((res) => {
				if (res.code === 0 && Array.isArray(res.data)) {
					const found = res.data.find((s) => String(s.store_id) === String(storeId));
					setStore(found ?? null);
				} else {
					setStoreError(`${res.msg || 'visible_stores failed'} (code=${res.code})`);
					setStore(null);
				}
			})
			.catch((e) => {
				setStoreError(e instanceof Error ? e.message : String(e));
				setStore(null);
			});
	}, [storeId]);

	useEffect(() => {
		if (!storeId) return;
		setDevices(null);
		setDeviceError(null);
		// iOS 用 fastDevice/channel_list 拿可播放通道（NVR 已展开为通道项）
		// (旧 /device/store_device_list_with_status 返回所有绑定记录含未绑通道，与 iOS 不一致)
		fetchFastChannels(storeId, { withOfflineTime: true, withYuzhiweiStatus: true })
			.then((chs) => setDevices(chs.map(fastChannelToDevice)))
			.catch((e) => {
				setDeviceError(e instanceof Error ? e.message : String(e));
				setDevices([]);
			});
	}, [storeId]);

	useEffect(() => {
		if (requestedStatus === 'offline' || requestedStatus === 'online') {
			setStatusFilter(requestedStatus);
		}
	}, [requestedStatus]);

	// 设备分组：iOS 把没有 name 的 NVR/老款多通道设备折叠为一项「我的 nvr 设备」
	// type_id=2 (NVR) 或 type_id=5 (老款) 且无 name → 归到 nvrFolded 组
	const { namedDevices, nvrFolded, counts } = useMemo(() => {
		const ds = devices ?? [];
		const named: Device[] = [];
		const folded: Device[] = [];
		for (const d of ds) {
			const isAudio = String(d.type_id) === AUDIO_TYPE_ID;
			const hasName = !!(d.name && String(d.name).trim());
			const isNvrLike = String(d.type_id) === '2' || String(d.type_id) === '5';
			if (isAudio) {
				named.push(d);
			} else if (!hasName && isNvrLike) {
				folded.push(d);
			} else {
				named.push(d);
			}
		}
		// name+在线优先
		named.sort((a, b) => {
			const aOn = String(a.status) === '1' ? 0 : 1;
			const bOn = String(b.status) === '1' ? 0 : 1;
			return aOn - bOn;
		});
		return {
			namedDevices: named,
			nvrFolded: folded,
			counts: {
				cameras: named.filter((d) => String(d.type_id) !== AUDIO_TYPE_ID).length,
				audios:  named.filter((d) => String(d.type_id) === AUDIO_TYPE_ID).length,
			},
		};
	}, [devices]);

	const filtered = useMemo(() => {
		return namedDevices.filter((d) => {
			if (statusFilter === 'online'  && String(d.status) !== '1') return false;
			if (statusFilter === 'offline' && String(d.status) === '1') return false;
			if (catFilter === 'camera' && String(d.type_id) === AUDIO_TYPE_ID) return false;
			if (catFilter === 'audio'  && String(d.type_id) !== AUDIO_TYPE_ID) return false;
			return true;
		});
	}, [namedDevices, statusFilter, catFilter]);
	const displayStoreName = store?.store || storeNameFallback;

	// 改名 Modal
	const [renameTarget, setRenameTarget] = useState<Device | null>(null);
	const [renameValue, setRenameValue] = useState('');
	const [renaming, setRenaming] = useState(false);
	const openRename = (d: Device) => {
		setRenameTarget(d);
		setRenameValue(d.name ?? '');
	};
	const submitRename = async () => {
		if (!renameTarget || !renameValue.trim()) return;
		setRenaming(true);
		const r = await renameChannel({
			deviceAutoId: renameTarget.device_id,
			channelId: Number(renameTarget.channel_id ?? 1),
			alias: renameValue.trim(),
		});
		setRenaming(false);
		if (r.ok) {
			antdMessage.success(t('rename.success'));
			// 本地立即更新（避免重新拉接口）
			setDevices((ds) => ds?.map((x) =>
				x.device_id === renameTarget.device_id ? { ...x, name: renameValue.trim() } : x,
			) ?? null);
			setRenameTarget(null);
		} else {
			antdMessage.error(`${t('rename.failed')}: ${r.msg ?? ''}`);
		}
	};

	// NVR 通道列表 Drawer
	const [channelsOf, setChannelsOf] = useState<Device | null>(null);
	const [channels, setChannels] = useState<Channel[] | null>(null);
	const openChannels = async (d: Device) => {
		setChannelsOf(d);
		setChannels(null);
		const cs = await fetchChannels(d.device_id);
		setChannels(cs);
	};

	// type_id=2 (NVR) / 5 (老款多通道) 走通道选择 drawer；type_id=3 IPC 直接播
	const isNvrLike = (d: Device) =>
		String(d.type_id) === '2' || String(d.type_id) === '5';

	const onDeviceClick = (d: Device) => {
		if (String(d.status) !== '1') {
			antdMessage.warning(t('store_detail.offline_unplayable'));
			return;
		}
		// fastDevice/channel_list 已把 NVR 展开为单通道，channel_id 直接可用
		// 但若条目本身就是 NVR 主体（很少见，多为残留绑定），仍走通道列表 drawer
		if (isNvrLike(d) && !d.channel_id) {
			void openChannels(d);
			return;
		}
		navigate(
			`/m/player?storeId=${storeId}&storeName=${encodeURIComponent(displayStoreName)}` +
			`&deviceSn=${d.sn ?? ''}&deviceLabel=${encodeURIComponent(deviceLabel(d))}` +
			`&channel=${d.channel_id ?? '1'}`,
		);
	};

	// 4 icon 行为
	const isFav = useGlobalStore((s) => s.favoriteStoreIds.includes(storeId ?? ''));
	const toggleFavorite = useGlobalStore((s) => s.toggleFavorite);
	const [apacFavoriteStoreIds, setApacFavoriteStoreIds] = useState<string[]>([]);
	const [apacFavoritePending, setApacFavoritePending] = useState(false);
	const apacIsFav = storeId ? apacFavoriteStoreIds.includes(storeId) : false;

	useEffect(() => {
		if (region !== 'apac') {
			setApacFavoriteStoreIds([]);
			return;
		}
		let alive = true;
		void fetchApacCollectedStoreIds().then((ids) => {
			if (alive) setApacFavoriteStoreIds(ids);
		});
		return () => {
			alive = false;
		};
	}, [region]);

	const toggleApacFavorite = async () => {
		if (!storeId || apacFavoritePending) return;
		const normalizedStoreId = String(storeId);
		const nextCollected = !apacFavoriteStoreIds.map(String).includes(normalizedStoreId);
		setApacFavoritePending(true);
		try {
			await setApacStoreCollected(normalizedStoreId, nextCollected);
			setApacFavoriteStoreIds((current) => {
				const ids = current.map(String);
				if (nextCollected) return ids.includes(normalizedStoreId) ? ids : [...ids, normalizedStoreId];
				return ids.filter((id) => id !== normalizedStoreId);
			});
		} catch {
			antdMessage.error(t('native.common.operation_failed'));
		} finally {
			setApacFavoritePending(false);
		}
	};

	// 电话呼叫 Modal
	const [phoneOpen, setPhoneOpen] = useState(false);
	const phones: string[] = [
		store?.store_phone,
		store?.shopowner_phone,
		...(Array.isArray(store?.more_phone) ? store!.more_phone : []),
	].filter((p): p is string => !!p && p.length >= 7);

	// 更多 Popover
	const [moreOpen, setMoreOpen] = useState(false);
	const [apacSettingsDevice, setApacSettingsDevice] = useState<Device | null>(null);
	const onDeleteStore = () => {
		Modal.confirm({
			title: t('store_detail.confirm_delete'),
			content: store?.store ?? '',
			okType: 'danger',
			okText: t('store_detail.confirm_delete_ok'),
			cancelText: t('common.cancel'),
			onOk: async () => {
				if (!storeId) return;
				const r = await deleteStore(storeId);
				if (r.ok) {
					antdMessage.success(t('store_detail.deleted'));
					navigate('/m/video', { replace: true });
				} else {
					antdMessage.error(`${t('store_detail.delete_failed')}: ${r.msg ?? ''}`);
				}
			},
		});
	};

	const onCopyAddr = () => {
		if (!store?.addr) return;
		navigator.clipboard?.writeText(store.addr).then(
			() => antdMessage.success(t('store_detail.copied')),
			() => antdMessage.warning(t('store_detail.copy_failed')),
		);
	};

	const formatCreateTime = () => {
		const raw = store?.create_time ?? store?.create_at;
		if (!raw) return '—';
		const ts = Number(raw);
		if (!Number.isNaN(ts) && ts > 0) {
			const d = new Date(ts * (String(raw).length <= 10 ? 1000 : 1));
			return formatDate(d, i18n.language);
		}
		return String(raw);
	};

	if (region === 'apac') {
		return (
			<ApacStoreDetailView
				store={store}
				storeId={storeId ?? ''}
				displayStoreName={displayStoreName}
				devices={namedDevices}
				devicesLoading={!devices}
				topTab={topTab}
				statusFilter={statusFilter}
				viewMode={viewMode}
				isFav={apacIsFav}
				favoritePending={apacFavoritePending}
				settingsDevice={apacSettingsDevice}
				phones={phones}
				phoneOpen={phoneOpen}
				onBack={() => navigate('/m/video')}
				onTabChange={setTopTab}
				onStatusChange={setStatusFilter}
				onToggleViewMode={() => setViewMode((current) => current === 'list' ? 'preview' : 'list')}
				onOpenMultiSelect={() => navigate(`/m/multi-select/${storeId}?storeName=${encodeURIComponent(displayStoreName)}`)}
				onCopyAddr={onCopyAddr}
				onOpenPhone={() => setPhoneOpen(true)}
				onClosePhone={() => setPhoneOpen(false)}
				onOpenRepair={() => navigate(
					`/m/repair?storeId=${storeId}&storeName=${encodeURIComponent(displayStoreName)}` +
					`&storePhone=${encodeURIComponent(store?.store_phone ?? '')}`
				)}
				onToggleFavorite={toggleApacFavorite}
				onOpenCamera={() => {
					const firstOnline = namedDevices.find((d) => String(d.status) === '1');
					if (firstOnline) onDeviceClick(firstOnline);
					else antdMessage.warning(t('mselect.no_online'));
				}}
				onOpenRepairRecord={() => navigate(`/m/repair-list?storeId=${storeId}`)}
				onDeviceClick={(device) => {
					if (String(device.status) === '1') onDeviceClick(device);
				}}
				onOpenSettings={setApacSettingsDevice}
				onCloseSettings={() => setApacSettingsDevice(null)}
				onRenameDevice={(device, name) => {
					setDevices((current) => current?.map((item) =>
						String(item.device_id) === String(device.device_id)
							? { ...item, name }
							: item,
					) ?? null);
					setApacSettingsDevice((current) =>
						current && String(current.device_id) === String(device.device_id)
							? { ...current, name }
							: current,
					);
				}}
			/>
		);
	}

	return (
		<MobileSubPageScaffold
			title={displayStoreName || t('tabbar.monitor')}
			onBack={() => navigate('/m/video')}
		>
			<SubPageCard style={{ marginBottom: 12 }}>
				<div style={{ padding: 14 }}>
					<div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: colors.text.secondary, fontSize: 13, lineHeight: 1.45 }}>
						<EnvironmentOutlined style={{ color: colors.primary.text, marginTop: 2 }} />
						<span style={{ flex: 1 }}>{store?.addr || '—'}</span>
						{store?.addr && (
							<Button
								type="text"
								size="small"
								icon={<CopyOutlined />}
								onClick={onCopyAddr}
								style={{ color: colors.primary.text, minHeight: 32 }}
							>
								{t('store_detail.copy')}
							</Button>
						)}
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.text.secondary, fontSize: 13, marginTop: 8 }}>
						<ClockCircleOutlined style={{ color: colors.primary.text }} />
						<span>{t('store_detail.created_at')}: {formatCreateTime()}</span>
					</div>
				</div>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
						borderTop: `1px solid ${colors.graphic.divider}`,
					}}
				>
					<StoreActionButton
						icon={<PhoneOutlined />}
						label={t('store_detail.call_owner')}
						onClick={() => setPhoneOpen(true)}
					/>
					<StoreActionButton
						icon={<ToolOutlined />}
						label={t('repair.title')}
						onClick={() => navigate(
							`/m/repair?storeId=${storeId}&storeName=${encodeURIComponent(displayStoreName)}` +
							`&storePhone=${encodeURIComponent(store?.store_phone ?? '')}`
						)}
					/>
					<StoreActionButton
						icon={isFav ? <StarFilled style={{ color: '#7A4F00' }} /> : <StarOutlined />}
						label={t('root.monitor.favorites')}
						onClick={() => storeId && toggleFavorite(storeId)}
						active={isFav}
					/>
					<Popover
						open={moreOpen}
						onOpenChange={setMoreOpen}
						trigger="click"
						placement="bottomRight"
						styles={{ body: { padding: 0 } }}
						content={
							<div style={{ minWidth: 160 }}>
								<MenuRow
									icon={<FileTextOutlined style={{ color: colors.primary.text }} />}
									label={t('store_detail.repair_log')}
									onClick={() => { setMoreOpen(false); navigate(`/m/repair-list?storeId=${storeId}`); }}
								/>
								<MenuRow
									icon={<CustomerServiceOutlined style={{ color: colors.status.warningText }} />}
									label={t('store_detail.service_hotline')}
									onClick={() => {
										setMoreOpen(false);
										if (typeof window !== 'undefined') window.location.href = `tel:${SERVICE_HOTLINE}`;
									}}
								/>
								<MenuRow
									icon={<DeleteOutlined style={{ color: colors.status.dangerText }} />}
									label={t('store_detail.delete_store')}
									onClick={() => { setMoreOpen(false); onDeleteStore(); }}
								/>
							</div>
						}
					>
						<div>
							<StoreActionButton icon={<MoreOutlined />} label={t('store_detail.more')} />
						</div>
					</Popover>
				</div>
			</SubPageCard>

			{storeError && <Alert type="warning" showIcon message={storeError} style={{ marginBottom: 12 }} />}
			{deviceError && <Alert type="warning" showIcon message={deviceError} style={{ marginBottom: 12 }} />}

			<SubPageCard style={{ marginBottom: 12 }}>
				<Tabs
					activeKey={topTab}
					onChange={(k) => setTopTab(k as typeof topTab)}
					tabBarStyle={{ margin: 0, paddingInline: 12 }}
					items={[
						{ key: 'devices',  label: t('store_detail.tab_devices') },
						{ key: 'overview', label: t('store_detail.tab_overview') },
					]}
				/>
			</SubPageCard>

			{topTab === 'overview' ? (
				storeId ? <StoreOverview storeId={storeId} /> : null
			) : (
				<>
					<SubPageCard>
						<div style={{ display: 'flex', alignItems: 'center', paddingInlineEnd: 8 }}>
							<Tabs
								activeKey={statusFilter}
								onChange={(k) => setStatusFilter(k as StatusFilter)}
								tabBarStyle={{ margin: 0, borderBottom: 'none', flex: 1, paddingInlineStart: 12 }}
								items={[
									{ key: 'all',     label: t('store_detail.devices_all') },
									{ key: 'online',  label: t('store_detail.devices_online') },
									{ key: 'offline', label: t('store_detail.devices_offline') },
								]}
							/>
							<Button type="text" disabled icon={<FilterOutlined style={{ color: colors.text.disabled, fontSize: 17 }} />} />
							<Button
								type="text"
								icon={viewMode === 'preview'
									? <UnorderedListOutlined style={{ color: colors.primary.text, fontSize: 17 }} />
									: <AppstoreOutlined    style={{ color: colors.primary.text, fontSize: 17 }} />}
								onClick={() => setViewMode((v) => v === 'list' ? 'preview' : 'list')}
							/>
						</div>

						<div style={{ padding: '6px 12px 10px', display: 'flex', gap: 8, borderTop: `1px solid ${colors.graphic.divider}`, overflowX: 'auto' }}>
							<CatChip
								active={catFilter === 'all'}
								onClick={() => setCatFilter('all')}
								label={t('store_detail.cat_all')}
							/>
							<CatChip
								active={catFilter === 'camera'}
								onClick={() => setCatFilter('camera')}
								label={`${t('store_detail.cat_camera')} ${counts.cameras}`}
							/>
							<CatChip
								active={catFilter === 'audio'}
								onClick={() => setCatFilter('audio')}
								label={`${t('store_detail.cat_audio')} ${counts.audios}`}
							/>
						</div>
					</SubPageCard>

					<SubPageSection>
						<div style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
							gap: 10,
						}}>
							{!devices && <Spin />}
							{devices && filtered.length === 0 && nvrFolded.length === 0 && <Empty description={t('common.no_data')} />}
							{filtered.map((d) => {
								const isOnline = String(d.status) === '1';
								const isAudio = String(d.type_id) === AUDIO_TYPE_ID;
								const statusLabel = t(isOnline ? 'store_detail.status_online' : 'store_detail.status_offline');
								if (viewMode === 'preview' && !isAudio && d.sn && isOnline) {
									return (
										<PreviewTile
											key={String(d.device_id)}
											device={d}
											onPlay={() => onDeviceClick(d)}
											onSettings={() => openRename(d)}
											onlineLabel={t('store_detail.status_online')}
										/>
									);
								}
								return (
									<Card
										key={String(d.device_id)}
										styles={{ body: { padding: '14px 16px' } }}
										style={{
											borderRadius: 8,
											borderColor: isOnline ? colors.graphic.border : colors.graphic.divider,
											background: isOnline ? colors.bg.card : '#f8f9fb',
											cursor: isOnline ? 'pointer' : 'default',
										}}
										onClick={() => onDeviceClick(d)}
									>
										<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
											{isAudio ? (
												<AudioOutlined style={{ fontSize: 22, color: isOnline ? colors.text.primary : colors.text.disabled }} />
											) : (
												<VideoCameraOutlined style={{ fontSize: 22, color: isOnline ? colors.text.primary : colors.text.disabled }} />
											)}
											<div style={{ flex: 1, minWidth: 0 }}>
												<Typography.Text style={{ display: 'block', fontSize: 16, color: isOnline ? colors.text.primary : colors.text.tertiary }}>
													{deviceLabel(d)}
												</Typography.Text>
												{!isOnline && (
													<Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
														{t('store_detail.offline_hint')}
													</Typography.Text>
												)}
											</div>
											<Tag color={isOnline ? 'success' : 'default'} style={{ marginInlineEnd: 0 }}>
												{statusLabel}
											</Tag>
											<Button
												type="text"
												icon={<SettingOutlined style={{ fontSize: 18, color: colors.graphic.muted }} />}
												onClick={(e) => { e.stopPropagation(); openRename(d); }}
											/>
										</div>
									</Card>
								);
							})}
							{nvrFolded.length > 0 && catFilter !== 'audio' && (
								<Card
									styles={{ body: { padding: '14px 16px' } }}
									style={{ borderRadius: 8, cursor: 'pointer', borderColor: colors.graphic.border }}
									onClick={() => nvrFolded[0] && openChannels(nvrFolded[0])}
								>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<VideoCameraOutlined style={{ fontSize: 22, color: colors.text.primary, marginInlineEnd: 12 }} />
										<Typography.Text style={{ flex: 1, fontSize: 16 }}>
											{t('store_detail.my_nvr_devices')}
										</Typography.Text>
										<Typography.Text type="secondary" style={{ marginInlineEnd: 8, fontSize: 13 }}>
											{nvrFolded.length}
										</Typography.Text>
										<RightIcon />
									</div>
								</Card>
							)}
						</div>
					</SubPageSection>
				</>
			)}

			{/* 改名 Modal */}
			<Modal
				title={t('rename.title')}
				open={!!renameTarget}
				onOk={submitRename}
				onCancel={() => setRenameTarget(null)}
				confirmLoading={renaming}
				okButtonProps={{ disabled: !renameValue.trim() }}
				destroyOnHidden
			>
				<Input
					value={renameValue}
					onChange={(e) => setRenameValue(e.target.value)}
					placeholder={t('rename.placeholder')}
					maxLength={32}
					onPressEnter={submitRename}
					autoFocus
				/>
			</Modal>

			{/* 呼叫店长 Modal */}
			<Modal
				open={phoneOpen}
				onCancel={() => setPhoneOpen(false)}
				footer={null}
				title={<div style={{ textAlign: 'center' }}>{t('store_detail.call_owner')}</div>}
				width={320}
				centered
			>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 0' }}>
					{phones.length === 0 && <Empty description={t('store_detail.no_phone')} />}
					{phones.map((p) => (
						<Button
							key={p}
							size="large"
							block
							style={{ borderRadius: 24, height: 44 }}
							onClick={() => {
								if (typeof window !== 'undefined') window.location.href = `tel:${p}`;
							}}
						>
							{p}
						</Button>
					))}
					<Button size="large" block onClick={() => setPhoneOpen(false)} style={{ borderRadius: 24, height: 44, background: '#f5f5f5', border: 'none' }}>
						{t('common.cancel')}
					</Button>
				</div>
			</Modal>

			{/* NVR 通道选择 Drawer */}
			<Drawer
				placement="bottom"
				height={420}
				open={!!channelsOf}
				onClose={() => { setChannelsOf(null); setChannels(null); }}
				title={
					<span>
						{channelsOf ? deviceLabel(channelsOf) : ''} · {t('channel.select')}
					</span>
				}
			>
				{!channels && <Spin />}
				{channels && channels.length === 0 && <Empty description={t('common.no_data')} />}
				{channels && channels.length > 0 && (
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
						{channels.map((ch) => (
							<Card
								key={String(ch.channel_id)}
								styles={{ body: { padding: '14px 12px' } }}
								style={{ borderRadius: 10, cursor: 'pointer', textAlign: 'center' }}
								onClick={() => {
									if (!channelsOf) return;
									navigate(
										`/m/player?storeId=${storeId}&storeName=${encodeURIComponent(store?.store ?? '')}` +
										`&deviceSn=${channelsOf.sn ?? ''}&deviceLabel=${encodeURIComponent(ch.alias || `ch ${ch.channel_id}`)}` +
										`&channel=${ch.channel_id}`,
									);
									setChannelsOf(null);
								}}
							>
								<VideoCameraOutlined style={{ fontSize: 28, color: PRIMARY }} />
								<div style={{ marginTop: 6, fontSize: 14, fontWeight: 600 }}>
									{ch.alias || `${t('channel.select')} ${ch.channel_id}`}
								</div>
								<div style={{ fontSize: 11, color: '#999' }}>ch {ch.channel_id}</div>
							</Card>
						))}
					</div>
				)}
			</Drawer>
		</MobileSubPageScaffold>
	);
}

function ApacStoreDetailView({
	store,
	storeId,
	displayStoreName,
	devices,
	devicesLoading,
	topTab,
	statusFilter,
	viewMode,
	isFav,
	favoritePending,
	settingsDevice,
	phones,
	phoneOpen,
	onBack,
	onTabChange,
	onStatusChange,
	onToggleViewMode,
	onOpenMultiSelect,
	onCopyAddr,
	onOpenPhone,
	onClosePhone,
	onOpenRepair,
	onToggleFavorite,
	onOpenCamera,
	onOpenRepairRecord,
	onDeviceClick,
	onOpenSettings,
	onCloseSettings,
	onRenameDevice,
}: {
	store: Store | null;
	storeId: string;
	displayStoreName: string;
	devices: Device[];
	devicesLoading: boolean;
	topTab: 'devices' | 'overview';
	statusFilter: StatusFilter;
	viewMode: 'list' | 'preview';
	isFav: boolean;
	favoritePending: boolean;
	settingsDevice: Device | null;
	phones: string[];
	phoneOpen: boolean;
	onBack: () => void;
	onTabChange: (tab: 'devices' | 'overview') => void;
	onStatusChange: (status: StatusFilter) => void;
	onToggleViewMode: () => void;
	onOpenMultiSelect: () => void;
	onCopyAddr: () => void;
	onOpenPhone: () => void;
	onClosePhone: () => void;
	onOpenRepair: () => void;
	onToggleFavorite: () => void;
	onOpenCamera: () => void;
	onOpenRepairRecord: () => void;
	onDeviceClick: (device: Device) => void;
	onOpenSettings: (device: Device) => void;
	onCloseSettings: () => void;
	onRenameDevice: (device: Device, name: string) => void;
}) {
	const { t } = useTranslation();
	const [moreOpen, setMoreOpen] = useState(false);
	const [dataSettingsOpen, setDataSettingsOpen] = useState(false);
	const allCount = devices.length;
	const onlineCount = devices.filter((device) => String(device.status) === '1').length;
	const offlineCount = Math.max(0, allCount - onlineCount);
	const filteredDevices = devices.filter((device) => {
		if (statusFilter === 'online') return String(device.status) === '1';
		if (statusFilter === 'offline') return String(device.status) !== '1';
		return true;
	});

	return (
		<div style={{ minHeight: '100dvh', background: '#f5f6f8', paddingBottom: 'calc(18px + env(safe-area-inset-bottom))' }}>
			<div style={{ background: '#060733', color: '#fff', padding: 'calc(10px + env(safe-area-inset-top)) 12px 16px' }}>
				<div style={{ display: 'flex', alignItems: 'center', height: 38 }}>
					<button type="button" onClick={onBack} style={apacHeaderIconButtonStyle}>
						<LeftOutlined />
					</button>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
						<button type="button" onClick={() => onTabChange('devices')} style={apacTopTabStyle(topTab === 'devices')}>{t('native.store.device_list')}</button>
						<button type="button" onClick={() => onTabChange('overview')} style={{ ...apacTopTabStyle(topTab === 'overview'), marginLeft: 22 }}>{t('native.store.data')}</button>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 4, width: 152, justifyContent: 'flex-end' }}>
						{topTab === 'overview' ? (
							<button
								type="button"
								aria-label={t('native.store.store_data_setting')}
								onClick={() => setDataSettingsOpen(true)}
								style={{ ...apacHeaderIconButtonStyle, color: PRIMARY, fontSize: 24 }}
							>
								<SettingOutlined />
							</button>
						) : (
							<>
								<ApacHeaderAction icon={<PhoneOutlined />} onClick={onOpenPhone} ariaLabel={t('store_detail.call_owner')} />
								<ApacHeaderAction icon={<ToolOutlined />} onClick={onOpenRepair} ariaLabel={t('native.profile.online_repair')} />
								<ApacHeaderAction
									icon={isFav ? <StarFilled /> : <StarOutlined />}
									onClick={onToggleFavorite}
									active={isFav}
									disabled={favoritePending}
									ariaLabel={t('store_video.favorite_only')}
								/>
								<Popover
									open={moreOpen}
									onOpenChange={setMoreOpen}
									trigger="click"
									placement="bottomRight"
									styles={{ body: { padding: 0 } }}
									content={
										<div style={{ minWidth: 178 }}>
											<MenuRow
												icon={<VideoCameraOutlined style={{ color: PRIMARY }} />}
												label={t('native.store.access_camera')}
												onClick={() => { setMoreOpen(false); onOpenCamera(); }}
											/>
											<MenuRow
												icon={<FileTextOutlined style={{ color: PRIMARY }} />}
												label={t('native.store.repair_record')}
												onClick={() => { setMoreOpen(false); onOpenRepairRecord(); }}
											/>
										</div>
									}
								>
									<button type="button" aria-label={t('store_detail.more')} style={apacHeaderIconButtonStyle}>
										<MoreOutlined />
									</button>
								</Popover>
							</>
						)}
					</div>
				</div>

				{topTab !== 'overview' && (
					<div style={{ padding: '12px 2px 0' }}>
						<div style={{ fontSize: mobileFontSize.title, fontWeight: mobileFontWeight.medium, lineHeight: 1.2 }}>{displayStoreName || store?.store || '-'}</div>
						<div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 12, color: 'rgba(255,255,255,0.82)', fontSize: 13, lineHeight: 1.45 }}>
							<EnvironmentOutlined style={{ marginRight: 6, marginTop: 2, flex: '0 0 auto' }} />
							<span style={{ flex: 1 }}>{store?.addr || '-'}</span>
							{store?.addr && (
								<button
									type="button"
									onClick={onCopyAddr}
									style={{ border: 0, background: 'transparent', color: '#fff', padding: '0 0 0 8px', fontSize: 13 }}
								>
									{t('native.common.copy')}
								</button>
							)}
						</div>
						<div style={{ display: 'flex', alignItems: 'center', marginTop: 10, color: 'rgba(255,255,255,0.78)', fontSize: 13 }}>
							<ClockCircleOutlined style={{ marginRight: 6 }} />
							<span>{t('native.store.store_creation_time')}: {formatApacDate(store?.create_time ?? store?.create_at)}</span>
						</div>
					</div>
				)}
			</div>

			{topTab === 'overview' ? (
				<ApacStoreDataView
					storeId={storeId}
					settingsOpen={dataSettingsOpen}
					onSettingsClose={() => setDataSettingsOpen(false)}
				/>
			) : (
				<div style={{ padding: '12px 12px 0' }}>
					<div style={{ background: '#fff', borderRadius: 4, padding: '0 8px', marginBottom: 10 }}>
						<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr) 34px 34px', alignItems: 'center', minHeight: 44, color: '#31343b', fontSize: 15 }}>
							<ApacStatusTab active={statusFilter === 'all'} label={`${t('native.common.all')} ${allCount}`} onClick={() => onStatusChange('all')} />
							<ApacStatusTab active={statusFilter === 'online'} label={`${t('native.common.online')} ${onlineCount}`} onClick={() => onStatusChange('online')} />
							<ApacStatusTab active={statusFilter === 'offline'} label={`${t('native.common.offline')} ${offlineCount}`} onClick={() => onStatusChange('offline')} />
							<button
								type="button"
								aria-label={t('native.common.filter')}
								onClick={onOpenMultiSelect}
								style={apacToolbarIconButtonStyle}
							>
								<FilterOutlined />
							</button>
							<button
								type="button"
								aria-label={viewMode === 'list' ? t('native.store.device_layout_preview') : t('native.store.device_layout_list')}
								onClick={onToggleViewMode}
								style={apacToolbarIconButtonStyle}
							>
								{viewMode === 'list' ? <AppstoreOutlined /> : <UnorderedListOutlined />}
							</button>
						</div>
					</div>
					{devicesLoading && <div style={{ padding: 34, textAlign: 'center' }}><Spin /></div>}
					{!devicesLoading && filteredDevices.length === 0 && <Empty description={t('native.common.no_data')} style={{ marginTop: 32 }} />}
					<div style={{ display: 'grid', gap: viewMode === 'preview' ? 12 : 8 }}>
						{filteredDevices.map((device) => (
							viewMode === 'preview' ? (
								<ApacDevicePreviewCard
									key={String(device.device_id)}
									device={device}
									onPlay={() => onDeviceClick(device)}
									onOpenSettings={() => onOpenSettings(device)}
								/>
							) : (
								<ApacDeviceListRow
									key={String(device.device_id)}
									device={device}
									onPlay={() => onDeviceClick(device)}
									onOpenSettings={() => onOpenSettings(device)}
								/>
							)
						))}
					</div>
				</div>
			)}

			<ApacCameraSettingsPanel
				device={settingsDevice}
				onClose={onCloseSettings}
				onRename={onRenameDevice}
			/>
			<ApacPhoneSheet
				open={phoneOpen}
				phones={phones}
				onClose={onClosePhone}
			/>
		</div>
	);
}

const apacToolbarIconButtonStyle: CSSProperties = {
	border: 0,
	background: 'transparent',
	color: PRIMARY,
	fontSize: 22,
	height: 44,
	display: 'grid',
	placeItems: 'center',
	cursor: 'pointer',
	padding: 0,
};

function ApacDeviceListRow({
	device,
	onPlay,
	onOpenSettings,
}: {
	device: Device;
	onPlay: () => void;
	onOpenSettings: () => void;
}) {
	const { t } = useTranslation();
	const online = isApacDeviceOnline(device);
	const isAudio = String(device.type_id) === AUDIO_TYPE_ID;
	return (
		<div
			role="button"
			tabIndex={online ? 0 : -1}
			onClick={() => online && onPlay()}
			onKeyDown={(event) => {
				if (!online) return;
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					onPlay();
				}
			}}
			style={{
				display: 'flex',
				alignItems: 'center',
				minHeight: 66,
				padding: '12px',
				background: '#fff',
				borderRadius: mobileRadius.sm,
				color: online ? '#20242b' : '#a7adb5',
				cursor: online ? 'pointer' : 'default',
			}}
		>
			<div style={{
				width: 38,
				height: 38,
				borderRadius: 4,
				background: online ? '#eef7ff' : '#f1f2f4',
				color: online ? '#2b7cff' : '#b5bbc3',
				display: 'grid',
				placeItems: 'center',
				fontSize: 20,
				marginRight: 12,
				flex: '0 0 auto',
			}}>
				{isAudio ? <AudioOutlined /> : <VideoCameraOutlined />}
			</div>
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={{ fontSize: mobileFontSize.title, fontWeight: mobileFontWeight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{deviceLabel(device)}
				</div>
			</div>
			<button
				type="button"
				aria-label={`${t('native.store.camera_setting')} ${deviceLabel(device)}`}
				onClick={(event) => {
					event.stopPropagation();
					onOpenSettings();
				}}
				style={{ border: 0, background: 'transparent', color: '#8d95a1', fontSize: 20, padding: 8, cursor: 'pointer' }}
			>
				<SettingOutlined />
			</button>
		</div>
	);
}

function ApacDevicePreviewCard({
	device,
	onPlay,
	onOpenSettings,
}: {
	device: Device;
	onPlay: () => void;
	onOpenSettings: () => void;
}) {
	const { t } = useTranslation();
	const online = isApacDeviceOnline(device);
	const isAudio = String(device.type_id) === AUDIO_TYPE_ID;
	const playable = online && !isAudio && Boolean(device.sn);
	const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [now, setNow] = useState(() => dayjs().format('MM-DD-YYYY ddd HH:mm:ss [UTC+08:00]'));

	useEffect(() => {
		const timer = setInterval(() => setNow(dayjs().format('MM-DD-YYYY ddd HH:mm:ss [UTC+08:00]')), 1000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		if (!videoEl || !playable) return;
		let player: MsePlayerInstance | null = null;
		let hls: Hls | null = null;
		let cancelled = false;
		const useFlv = msePlayer.isSupported();
		const start = async () => {
			try {
				const res = await callInternal<{ play_url: string }>({
					short_name: 'base',
					action: '/play/weburl',
					query: {
						device_id: device.sn!,
						channel_idx: Number(device.channel_id ?? 1),
						rate: 1000,
						play_type: useFlv ? 'hflv' : 'hls',
					},
					av: '1',
				});
				if (cancelled) return;
				if (!res.data?.play_url) {
					setError(`code=${res.code}`);
					return;
				}
				const needsProxy = res.data.play_url.startsWith('http://')
					&& (window.location.protocol === 'https:' || isCapacitor());
				const finalUrl = needsProxy
					? apiUrl(`/api/stream?url=${encodeURIComponent(res.data.play_url)}`)
					: res.data.play_url;
				videoEl.muted = true;
				if (useFlv) {
					player = msePlayer.createPlayer(
						{ type: 'flv', url: finalUrl, isLive: true, cors: true },
						{ enableWorker: false, enableStashBuffer: false, stashInitialSize: 128 },
					);
					player.attachMediaElement(videoEl);
					player.load();
					const ret = player.play();
					if (ret && typeof (ret as Promise<void>).then === 'function') {
						(ret as Promise<void>).catch(() => {});
					}
				} else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
					videoEl.src = finalUrl;
					void videoEl.play().catch(() => {});
				} else if (Hls.isSupported()) {
					hls = new Hls();
					hls.loadSource(finalUrl);
					hls.attachMedia(videoEl);
					hls.on(Hls.Events.MANIFEST_PARSED, () => { void videoEl.play().catch(() => {}); });
				} else {
					setError('unsupported');
				}
			} catch (e) {
				if (!cancelled) setError((e as Error).message);
			}
		};
		void start();
		return () => {
			cancelled = true;
			if (player) {
				try { player.pause(); player.unload(); player.detachMediaElement(); player.destroy(); } catch {}
			}
			if (hls) {
				try { hls.destroy(); } catch {}
			}
		};
	}, [device.channel_id, device.sn, playable, videoEl]);

	const label = deviceLabel(device);
	const offlineTime = formatApacOfflineTime(device.offline_time);
	const offlineDuration = formatApacOfflineDuration(device.offline_time);
	return (
		<div
			onClick={() => online && onPlay()}
			style={{
				background: '#fff',
				borderRadius: mobileRadius.md,
				boxShadow: mobileShadow.soft,
				overflow: 'hidden',
				cursor: online ? 'pointer' : 'default',
			}}
		>
			<div style={{ position: 'relative', aspectRatio: '16 / 9', background: online ? '#111' : '#c9c9c9', overflow: 'hidden' }}>
				{playable && (
					<video
						ref={setVideoEl}
						autoPlay
						playsInline
						muted
						poster={BLANK_VIDEO_POSTER}
						style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#000' }}
					/>
				)}
				{online ? (
					<>
						<div style={apacPreviewTimestampStyle}>{now}</div>
						<div style={apacPreviewStatusPillStyle(true)}>
							<span style={apacPreviewDotStyle('#52C41A')} />
							{t('native.common.online')}
						</div>
					</>
				) : (
					<div style={{ position: 'absolute', inset: 0, color: '#333' }}>
						<div style={apacPreviewStatusPillStyle(false)}>
							<span style={apacPreviewDotStyle('#ff3b3b')} />
							{t('native.common.offline')}
						</div>
						<div style={{ position: 'absolute', top: '31%', left: 0, right: 0, textAlign: 'center', color: '#f05a5a', fontSize: mobileFontSize.body }}>
							{t('native.store.device_is_offline')}
						</div>
						<div style={{ position: 'absolute', top: '47%', left: 36, right: 16, color: '#333', fontSize: mobileFontSize.bodySmall, lineHeight: 1.45 }}>
							{offlineTime && <div>{t('native.store.offline_time')}: {offlineTime}</div>}
							{offlineDuration && <div>{t('native.store.offline_duration')}: {offlineDuration}</div>}
						</div>
					</div>
				)}
				{error && (
					<div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 12, opacity: 0.7 }}>
						{error}
					</div>
				)}
			</div>
			<div style={{ display: 'flex', alignItems: 'center', minHeight: 56, padding: '10px 12px' }}>
				{isAudio ? (
					<AudioOutlined style={{ fontSize: 22, color: online ? '#6a6f77' : '#b9bdc4', marginRight: 10 }} />
				) : (
					<VideoCameraOutlined style={{ fontSize: 22, color: online ? '#6a6f77' : '#b9bdc4', marginRight: 10 }} />
				)}
				<span style={{ flex: 1, minWidth: 0, color: online ? mobileColor.text : '#a7adb5', fontSize: mobileFontSize.title, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{label}
				</span>
				<button
					type="button"
					aria-label={`${t('native.store.camera_setting')} ${label}`}
					onClick={(event) => {
						event.stopPropagation();
						onOpenSettings();
					}}
					style={{ border: 0, background: 'transparent', color: '#8d95a1', fontSize: 24, padding: 8, cursor: 'pointer' }}
				>
					<SettingOutlined />
				</button>
			</div>
		</div>
	);
}

const apacPreviewTimestampStyle: CSSProperties = {
	position: 'absolute',
	top: 8,
	left: 10,
	color: '#fff',
	fontSize: 18,
	lineHeight: 1,
	fontWeight: 600,
	textShadow: '1px 1px 0 rgba(0,0,0,0.65)',
};

function apacPreviewStatusPillStyle(online: boolean): CSSProperties {
	return {
		position: 'absolute',
		top: online ? 30 : 22,
		left: online ? 12 : 22,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		color: '#fff',
		background: online ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.45)',
		borderRadius: 14,
		padding: '2px 10px',
		fontSize: online ? 18 : 16,
		lineHeight: 1.1,
	};
}

function apacPreviewDotStyle(color: string): CSSProperties {
	return {
		width: 8,
		height: 8,
		borderRadius: '50%',
		background: color,
		display: 'inline-block',
		flex: '0 0 auto',
	};
}

function isApacDeviceOnline(device: Device): boolean {
	return String(device.status) === '1';
}

function formatApacOfflineTime(raw?: string | null): string {
	if (!raw) return '';
	const parsed = dayjs(raw);
	return parsed.isValid() ? parsed.format('DD/MM/YYYY') : String(raw);
}

function formatApacOfflineDuration(raw?: string | null): string {
	if (!raw) return '';
	const parsed = dayjs(raw);
	if (!parsed.isValid()) return '';
	const totalMinutes = Math.max(dayjs().diff(parsed, 'minute'), 0);
	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);
	const minutes = totalMinutes % 60;
	return `${days}Day${hours}Hour${minutes}Minute`;
}

function ApacStatusTab({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				border: 0,
				borderBottom: active ? `2px solid ${PRIMARY}` : '2px solid transparent',
				background: 'transparent',
				color: active ? PRIMARY : '#30343b',
				fontSize: 14,
				height: 44,
				padding: 0,
			}}
		>
			{label}
		</button>
	);
}

function ApacHeaderAction({
	icon,
	onClick,
	active = false,
	disabled = false,
	ariaLabel,
}: {
	icon: React.ReactNode;
	onClick: () => void;
	active?: boolean;
	disabled?: boolean;
	ariaLabel?: string;
}) {
	return (
		<button
			type="button"
			aria-label={ariaLabel}
			disabled={disabled}
			onClick={onClick}
			style={{
				...apacHeaderIconButtonStyle,
				color: active ? '#ffce73' : '#fff',
				opacity: disabled ? 0.55 : 1,
			}}
		>
			{icon}
		</button>
	);
}

type ApacCameraSettingsStep = 'menu' | 'name' | 'cloud' | 'local' | 'wifi' | 'unused';

function ApacCameraSettingsPanel({
	device,
	onClose,
	onRename,
}: {
	device: Device | null;
	onClose: () => void;
	onRename: (device: Device, name: string) => void;
}) {
	const { t } = useTranslation();
	const [step, setStep] = useState<ApacCameraSettingsStep>('menu');
	const [draftName, setDraftName] = useState('');
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!device) return;
		setStep('menu');
		setDraftName(deviceLabel(device));
	}, [device]);

	if (!device) return null;
	const title = step === 'menu'
		? t('native.store.camera_setting')
		: step === 'name'
			? t('native.store.camera_name')
			: step === 'cloud'
				? t('native.store.cloud_storage')
				: step === 'local'
					? t('native.store.local_storage')
					: step === 'wifi'
						? t('native.store.connect_camera_to_store')
						: t('native.store.no_longer_use');
	const goBack = () => {
		if (step === 'menu') onClose();
		else setStep('menu');
	};
	const confirmName = async () => {
		const nextName = draftName.trim();
		if (!nextName || saving) return;
		setSaving(true);
		try {
			const result = await renameChannel({
				deviceAutoId: device.device_id,
				channelId: Number(device.channel_id ?? 1),
				alias: nextName,
			});
			if (result.ok) {
				onRename(device, nextName);
				setStep('menu');
			} else {
				antdMessage.warning(t('native.common.operation_failed'));
			}
		} finally {
			setSaving(false);
		}
	};
	return (
		<div style={{
			position: 'fixed',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			zIndex: 1300,
			background: '#f5f6f8',
			color: '#20242b',
		}}>
			<div style={{
				height: 'calc(48px + env(safe-area-inset-top))',
				paddingTop: 'env(safe-area-inset-top)',
				background: '#fff',
				display: 'grid',
				gridTemplateColumns: '72px 1fr 96px',
				alignItems: 'center',
				borderBottom: '1px solid #edf0f3',
			}}>
				<button
					type="button"
					onClick={goBack}
					style={{ border: 0, background: 'transparent', color: PRIMARY, fontSize: 22, height: 48, textAlign: 'left', paddingLeft: 14 }}
				>
					<LeftOutlined />
				</button>
				<div style={{ textAlign: 'center', fontSize: mobileFontSize.topBar, fontWeight: mobileFontWeight.medium }}>{title}</div>
				{step === 'name' ? (
					<button
						type="button"
						onClick={() => void confirmName()}
						disabled={saving}
						style={{ border: 0, background: 'transparent', color: PRIMARY, fontSize: 16, height: 48, paddingRight: 10 }}
					>
						{t('native.common.confirm')}
					</button>
				) : (
					<div>{(step === 'wifi' || step === 'unused') && <MoreOutlined style={{ color: PRIMARY, fontSize: 22 }} />}</div>
				)}
			</div>
			{step === 'menu' && (
				<div style={{ marginTop: 10, background: '#fff' }}>
					<ApacSettingsRow label={t('native.store.camera_name')} value={deviceLabel(device)} onClick={() => setStep('name')} />
					<ApacSettingsRow label={t('native.store.cloud_storage')} onClick={() => setStep('cloud')} />
					<ApacSettingsRow label={t('native.store.local_storage')} onClick={() => setStep('local')} />
					<ApacSettingsRow label={t('native.store.connect_other_wifi')} onClick={() => setStep('wifi')} />
					<ApacSettingsRow label={t('native.store.no_longer_use')} onClick={() => setStep('unused')} />
				</div>
			)}
			{step === 'name' && (
				<div style={{ background: '#fff', borderTop: '1px solid #edf0f3', borderBottom: '1px solid #d9d9d9', marginTop: 34 }}>
					<Input
						value={draftName}
						onChange={(event) => setDraftName(event.target.value)}
						variant="borderless"
						style={{ height: 58, fontSize: mobileFontSize.topBarLarge, color: '#8a8f96', padding: '0 22px' }}
					/>
				</div>
			)}
			{step === 'cloud' && <ApacCloudStoragePage />}
			{step === 'local' && <ApacLocalStoragePage />}
			{step === 'wifi' && <ApacConnectCameraPage title={t('native.store.connect_camera_to_store')} />}
			{step === 'unused' && <ApacConnectCameraPage title={t('native.store.no_longer_use')} />}
		</div>
	);
}

function ApacCloudStoragePage() {
	const { t } = useTranslation();
	return (
		<div style={{ background: '#eff1f6', minHeight: 'calc(100dvh - 48px - env(safe-area-inset-top))' }}>
			<div style={{
				height: 225,
				background: 'linear-gradient(130deg, #e7faff 0%, #1c2a36 46%, #05090f 100%)',
				color: '#fff',
				display: 'grid',
				alignContent: 'center',
				justifyItems: 'end',
				paddingRight: 26,
				gap: 6,
				fontSize: mobileFontSize.topBarLarge,
				lineHeight: 1.22,
				textShadow: '0 1px 2px rgba(0,0,0,0.45)',
			}}>
				<div>{t('native.store.cloud_storage_banner_auto_detect')}</div>
				<div>{t('native.store.cloud_storage_banner_look_back')}</div>
				<div>{t('native.store.cloud_storage_banner_encryption')}</div>
			</div>
			<div style={{ display: 'grid', justifyItems: 'center', paddingTop: 92 }}>
				<div style={{
					width: 150,
					height: 150,
					borderRadius: '50%',
					background: '#61c6bd',
					display: 'grid',
					placeItems: 'center',
					color: '#fff',
					fontSize: 66,
				}}>
					<CloudUploadOutlined />
				</div>
				<div style={{ marginTop: 74, color: '#85878c', fontSize: 15, lineHeight: 1.42, textAlign: 'center', fontWeight: 500 }}>
					<div>{t('native.store.cloud_storage_unavailable_line1')}</div>
					<div>{t('native.store.cloud_storage_unavailable_line2')}</div>
				</div>
				<div style={{ marginTop: 42, color: '#85878c', fontSize: 15, lineHeight: 1.42, textAlign: 'center', fontWeight: 500 }}>
					{t('native.store.contact_administrator')}
				</div>
			</div>
		</div>
	);
}

function ApacLocalStoragePage() {
	const { t } = useTranslation();
	const warnUnavailable = () => {
		void antdMessage.warning(t('native.store.setting_unavailable'));
	};
	return (
		<div style={{ minHeight: 'calc(100dvh - 48px - env(safe-area-inset-top))', background: '#fff', color: '#34363a', padding: '28px 22px 28px' }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 15, marginBottom: 52 }}>
				<span>{t('native.store.memory_card_recording')}</span>
				<button
					type="button"
					aria-label={t('native.store.memory_card_recording')}
					onClick={warnUnavailable}
					style={{ width: 60, height: 31, border: 0, borderRadius: 20, background: PRIMARY, position: 'relative', display: 'inline-block', padding: 0 }}
				>
					<span style={{ width: 25, height: 25, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, right: 4 }} />
				</button>
			</div>
			<div style={{ color: '#666b72', fontSize: 14, marginBottom: 26 }}>{t('native.store.memory_card_type')}</div>
			<div style={{ display: 'grid', gap: 22, paddingLeft: 46, fontSize: 14 }}>
				<ApacRadioLine active label={t('native.store.full_storage')} />
				<ApacRadioLine label={t('native.store.mobile_detection_storage')} />
				<button
					type="button"
					onClick={warnUnavailable}
					style={{ display: 'flex', alignItems: 'center', border: 0, background: 'transparent', color: 'inherit', font: 'inherit', padding: 0, textAlign: 'left' }}
				>
					<ApacRadioLine label={t('native.store.set_storage_schedule')} />
					<RightOutlined style={{ marginLeft: 'auto', color: '#c8c8c8', fontSize: 20 }} />
				</button>
			</div>
			<div style={{ marginTop: 70, fontSize: 15 }}>{t('native.store.internal_storage_space')}</div>
			<div style={{ display: 'flex', gap: 24, color: '#999', fontSize: 13, marginTop: 22 }}>
				<span>{t('native.store.storage_available', { value: '--' })}</span>
				<span>{t('native.store.storage_total', { value: '--' })}</span>
			</div>
			<div style={{ height: 7, borderRadius: 4, background: PRIMARY, marginTop: 18 }} />
			<button
				type="button"
				onClick={warnUnavailable}
				style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: 68, border: 0, background: 'transparent', color: 'inherit', fontSize: 15, padding: 0, textAlign: 'left' }}
			>
				<span>{t('native.store.format_memory_card')}</span>
				<span style={{ marginLeft: 14, background: '#c5cbd3', color: '#fff', borderRadius: 16, padding: '2px 14px', fontSize: 15 }}>{t('native.store.setting_unavailable')}</span>
				<RightOutlined style={{ marginLeft: 'auto', color: '#c8c8c8', fontSize: 20 }} />
			</button>
			<div style={{ color: '#777', fontSize: mobileFontSize.bodySmall, lineHeight: 1.4, marginTop: 28 }}>
				{t('native.store.memory_card_format_hint')}
			</div>
			<div style={{ color: '#ff3b3b', fontSize: mobileFontSize.bodySmall, lineHeight: 1.4, marginTop: 42 }}>
				{t('native.store.memory_card_non_standard_tip')}
			</div>
			<button
				type="button"
				onClick={warnUnavailable}
				style={{
					position: 'fixed',
					left: 82,
					right: 82,
					bottom: 'calc(50px + env(safe-area-inset-bottom))',
					height: 60,
					borderRadius: 32,
					border: 0,
					background: PRIMARY,
					color: '#fff',
					fontSize: mobileFontSize.topBarLarge,
				}}
			>
				{t('native.common.save')}
			</button>
		</div>
	);
}

function ApacRadioLine({ active = false, label }: { active?: boolean; label: string }) {
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
			<span style={{
				width: 24,
				height: 24,
				borderRadius: '50%',
				border: `2px solid ${active ? PRIMARY : '#b8b8b8'}`,
				display: 'grid',
				placeItems: 'center',
				flex: '0 0 auto',
			}}>
				{active && <span style={{ width: 12, height: 12, borderRadius: '50%', background: PRIMARY }} />}
			</span>
			<span>{label}</span>
		</div>
	);
}

function ApacConnectCameraPage({ title }: { title: string }) {
	const { t } = useTranslation();
	return (
		<div style={{ minHeight: 'calc(100dvh - 48px - env(safe-area-inset-top))', background: '#fff', display: 'grid', placeItems: 'center', padding: 24 }}>
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<div style={{ color: '#8b929c', fontSize: mobileFontSize.bodySmall, lineHeight: 1.45 }}>
						<div>{title}</div>
						<div>{t('native.store.setting_unavailable')}</div>
					</div>
				}
			/>
		</div>
	);
}

function ApacPhoneSheet({ open, phones, onClose }: { open: boolean; phones: string[]; onClose: () => void }) {
	const { t } = useTranslation();
	if (!open) return null;
	return (
		<div style={{
			position: 'fixed',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			zIndex: 1300,
			background: 'rgba(0, 0, 0, 0.18)',
		}}>
			<button
				type="button"
				aria-label={t('native.common.cancel')}
				onClick={onClose}
				style={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					left: 0,
					border: 0,
					background: 'transparent',
					padding: 0,
				}}
			/>
			<div style={{
				position: 'absolute',
				left: 0,
				right: 0,
				bottom: 0,
				background: '#fff',
				padding: '8px 12px calc(16px + env(safe-area-inset-bottom))',
				boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.12)',
			}}>
				<div style={{ display: 'grid', gap: 8 }}>
					{phones.length === 0 ? (
						<div style={{ padding: '18px 0', textAlign: 'center', color: '#8b929c' }}>{t('native.common.no_data')}</div>
					) : phones.map((phone) => (
						<button
							key={phone}
							type="button"
							onClick={() => {
								onClose();
								if (typeof window !== 'undefined') window.location.href = `tel:${phone}`;
							}}
							style={{ border: 0, background: '#fff', color: '#20242b', padding: '14px 10px', fontSize: 16, textAlign: 'center' }}
						>
							{phone}
						</button>
					))}
					<button
						type="button"
						onClick={onClose}
						style={{ border: 0, borderTop: '1px solid #edf0f3', background: '#fff', color: '#20242b', padding: '14px 10px', fontSize: 16, textAlign: 'center' }}
					>
						{t('native.common.cancel')}
					</button>
				</div>
			</div>
		</div>
	);
}

function ApacSettingsRow({
	label,
	value,
	danger = false,
	onClick,
}: {
	label: string;
	value?: string;
	danger?: boolean;
	onClick?: () => void;
}) {
	return (
		<button type="button" onClick={onClick} style={{
			display: 'flex',
			alignItems: 'center',
			width: '100%',
			minHeight: 52,
			padding: '0 14px',
			borderBottom: '1px solid #edf0f3',
			borderTop: 0,
			borderLeft: 0,
			borderRight: 0,
			background: '#fff',
			color: danger ? '#ef4444' : '#20242b',
			fontSize: 15,
			textAlign: 'left',
		}}>
			<span style={{ flex: 1 }}>{label}</span>
			{value && <span style={{ color: '#8b929c', marginRight: 8 }}>{value}</span>}
			{!danger && <RightOutlined style={{ color: '#b5bbc3', fontSize: 12 }} />}
		</button>
	);
}

function formatApacDate(raw: string | number | undefined) {
	if (!raw) return '-';
	const ts = Number(raw);
	if (!Number.isNaN(ts) && ts > 0) {
		const date = dayjs(ts * (String(raw).length <= 10 ? 1000 : 1));
		return date.isValid() ? date.format('DD/MM/YYYY') : String(raw);
	}
	const date = dayjs(raw);
	return date.isValid() ? date.format('DD/MM/YYYY') : String(raw);
}

const apacHeaderIconButtonStyle: React.CSSProperties = {
	border: 0,
	background: 'transparent',
	color: '#fff',
	width: 30,
	height: 34,
	display: 'grid',
	placeItems: 'center',
	fontSize: 18,
	padding: 0,
};

function apacTopTabStyle(active: boolean): React.CSSProperties {
	return {
		border: 0,
		borderBottom: active ? `3px solid ${PRIMARY}` : '3px solid transparent',
		background: 'transparent',
		color: '#fff',
		fontSize: mobileFontSize.title,
		fontWeight: active ? mobileFontWeight.semibold : mobileFontWeight.medium,
		padding: '8px 0 6px',
		whiteSpace: 'nowrap',
	};
}

function MenuRow({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
	return (
		<div
			onClick={onClick}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 10,
				padding: '12px 14px',
				cursor: 'pointer',
				borderBottom: '1px solid #f5f5f5',
			}}
		>
			<span style={{ fontSize: 18 }}>{icon}</span>
			<span style={{ fontSize: 14 }}>{label}</span>
		</div>
	);
}

function StoreActionButton({
	icon,
	label,
	onClick,
	active,
}: {
	icon: React.ReactNode;
	label: string;
	onClick?: () => void;
	active?: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				border: 0,
				borderInlineStart: `1px solid ${colors.graphic.divider}`,
				background: active ? colors.primary.soft : colors.bg.card,
				minHeight: 76,
				padding: '10px 4px',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 6,
				color: active ? colors.status.warningText : colors.primary.text,
				cursor: onClick ? 'pointer' : 'default',
			}}
		>
			<span style={{ fontSize: 21, lineHeight: 1 }}>{icon}</span>
			<span style={{ maxWidth: '100%', color: colors.text.secondary, fontSize: 11, lineHeight: 1.15, textAlign: 'center' }}>
				{label}
			</span>
		</button>
	);
}

/** 卡片预览模式：每条设备渲染一个小 flv.js 实时画面 + 在线徽章 + 名称行 */
function PreviewTile({
	device,
	onPlay,
	onSettings,
	onlineLabel,
}: {
	device: Device;
	onPlay: () => void;
	onSettings: () => void;
	onlineLabel: string;
}) {
	const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [now, setNow] = useState(() => dayjs().format('YYYY-MM-DD ddd HH:mm:ss'));

	useEffect(() => {
		const t = setInterval(() => setNow(dayjs().format('YYYY-MM-DD ddd HH:mm:ss')), 1000);
		return () => clearInterval(t);
	}, []);

	useEffect(() => {
		if (!videoEl || !device.sn) return;
		let player: MsePlayerInstance | null = null;
		let hls: Hls | null = null;
		let cancelled = false;
		// iOS Safari/微信不支持 MSE → 降级 HLS（同 UluVideoPlayer / MultiView）
		const useFlv = msePlayer.isSupported();
		const start = async () => {
			try {
				const res = await callInternal<{ play_url: string }>({
					short_name: 'base',
					action: '/play/weburl',
					query: {
						device_id: device.sn!,
						channel_idx: Number(device.channel_id ?? 1),
						rate: 1000,
						play_type: useFlv ? 'hflv' : 'hls',
					},
					av: '1',
				});
				if (cancelled || !res.data?.play_url) {
					if (!cancelled) setError(`code=${res.code}`);
					return;
				}
				// HTTPS / Capacitor 包壳 + http:// 流 → 走后端 /api/stream 代理
				const needsProxy = res.data.play_url.startsWith('http://')
					&& (window.location.protocol === 'https:' || isCapacitor());
				const finalUrl = needsProxy
					? apiUrl(`/api/stream?url=${encodeURIComponent(res.data.play_url)}`)
					: res.data.play_url;
				videoEl.muted = true;  // iOS autoplay 必需
				if (useFlv) {
					player = msePlayer.createPlayer(
						{ type: 'flv', url: finalUrl, isLive: true, cors: true },
						{ enableWorker: false, enableStashBuffer: false, stashInitialSize: 128 },
					);
					player.attachMediaElement(videoEl);
					player.load();
					const ret = player.play();
					if (ret && typeof (ret as Promise<void>).then === 'function') {
						(ret as Promise<void>).catch(() => {});
					}
				} else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
					videoEl.src = finalUrl;
					void videoEl.play().catch(() => {});
				} else if (Hls.isSupported()) {
					hls = new Hls();
					hls.loadSource(finalUrl);
					hls.attachMedia(videoEl);
					hls.on(Hls.Events.MANIFEST_PARSED, () => { void videoEl.play().catch(() => {}); });
				} else {
					setError('unsupported');
					return;
				}
			} catch (e) {
				if (!cancelled) setError((e as Error).message);
			}
		};
		void start();
		return () => {
			cancelled = true;
			if (player) {
				try { player.pause(); player.unload(); player.detachMediaElement(); player.destroy(); } catch {}
			}
			if (hls) {
				try { hls.destroy(); } catch {}
			}
		};
	}, [videoEl, device.sn, device.channel_id]);

	return (
		<div
			onClick={onPlay}
			style={{
				borderRadius: 10,
				overflow: 'hidden',
				background: '#fff',
				boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
				cursor: 'pointer',
			}}
		>
			<div style={{ position: 'relative', background: '#000', aspectRatio: '16 / 9' }}>
				<video
					ref={setVideoEl}
					autoPlay
					playsInline
					muted
					poster={BLANK_VIDEO_POSTER}
					style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#000' }}
				/>
				<div style={{
					position: 'absolute',
					top: 6, left: 8,
					color: '#fff',
					fontSize: 11,
					textShadow: '1px 1px 0 rgba(0,0,0,0.5)',
					display: 'flex',
					alignItems: 'center',
					gap: 4,
				}}>
					<span style={{ width: 7, height: 7, borderRadius: '50%', background: '#52C41A', display: 'inline-block' }} />
					{now}
				</div>
				<div style={{
					position: 'absolute',
					top: 6, right: 8,
					background: 'rgba(0,0,0,0.45)',
					color: '#fff',
					fontSize: 11,
					padding: '2px 8px',
					borderRadius: 10,
				}}>● {onlineLabel}</div>
				{error && (
					<div style={{
						position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						color: '#fff', fontSize: 12, opacity: 0.7,
					}}>{error}</div>
				)}
				<div style={{
					position: 'absolute', bottom: 6, left: 8,
					color: '#fff', fontSize: 12,
					textShadow: '1px 1px 0 rgba(0,0,0,0.5)',
				}}>{deviceLabel(device)}</div>
			</div>
			<div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px' }}>
				<VideoCameraOutlined style={{ fontSize: 18, color: '#444', marginInlineEnd: 8 }} />
				<span style={{ flex: 1, fontSize: 15 }}>{deviceLabel(device)}</span>
				<Button
					type="text"
					icon={<SettingOutlined style={{ fontSize: 18, color: '#888' }} />}
					onClick={(e) => { e.stopPropagation(); onSettings(); }}
				/>
			</div>
		</div>
	);
}

function CatChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
	return (
		<span
			onClick={onClick}
			style={{
				padding: '4px 14px',
				borderRadius: 14,
				fontSize: 13,
				background: active ? '#FFEFE3' : 'transparent',
				color: active ? PRIMARY : '#666',
				fontWeight: active ? 600 : 400,
				cursor: 'pointer',
				userSelect: 'none',
			}}
		>
			{label}
		</span>
	);
}
